import "./TemplateHierarchy.less";

import { message, notification, Button, Icon, Modal, Spin, Switch } from "antd";
import arrayMove from "array-move";
import { cloneDeep } from "lodash";
import * as React from "react";
import { RouteComponentProps } from "react-router-dom";

import { ComponentTemplateApi, SectionTemplateApi } from "lib/api";
import { CancellationToken } from "lib/api/base";
import { IBaseProps } from "lib/data/interfaces";
import { ComponentTemplate, SectionTemplate } from "lib/data/model";
import { MultipleItemRequirement, Requirement, RequirementItem, SingleItemRequirement } from "lib/data/model";
import { MultipleItemRequirementType } from "lib/data/model/enumerations/MultipleItemRequirementType";
import { StateService } from "lib/services";
import { MultipleItemConfiguration } from "./components/MultipleItemConfiguration";

import { InfoQuestion } from "components/general/StandardButtons/InfoQuestion";
import { TransitionRoute } from "components/general/TransitionRoute";
import { Requiredness } from "lib/data/model/enumerations";
import { TemplateRequirementSelector, TemplateTitle } from "../components";
import { GenericTemplateSelector } from "../components/GenericTemplateSelector/GenericTemplateSelector";
import { GenericTemplateSelectorSingle } from "../components/GenericTemplateSelector/GenericTemplateSelectorSingle";
import { SortableContainer } from "../components/TemplateRequirementSelector/SortableContainer";
import { Breadcrumb } from "./components/Breadcrumb";
import { MetaData } from "./components/Metadata";
import { NewRequirement } from "./components/NewRequirement";

interface ITemplateHierarchyProps extends IBaseProps, RouteComponentProps<any> {

}

interface ITemplateHierarchyState {
	genericComponentTemplates: ComponentTemplate[];
	visibleSection: "template-requirement-selector" | "generic-template-selector" | "requirement-configuration" | "requirement-configuration";
	sectionTemplate?: SectionTemplate;
	newTemplateList: ComponentTemplate[];
	selectedMultiItemTemplates: ComponentTemplate[];
	newMultiItemRequirementItems: Array<RequirementItem<ComponentTemplate>>;
	multiItemRequirementPhase: "selection" | "configuration";
	loading: boolean;
	waitingForUpdate: boolean;
	creatingMultipleItemRequirement: boolean;
	creatingSingleItemRequirement: boolean;
	editingMetaData: boolean;
	multiItemTemplateUnderEdit?: MultipleItemRequirement<ComponentTemplate>;
}


export class TemplateHierarchy extends React.Component<ITemplateHierarchyProps, ITemplateHierarchyState> {

	public static readonly BasePath: string = "/admin/templatehierarchy";


	private apiCancellationToken: CancellationToken = new CancellationToken();

	private sectionTemplate?: SectionTemplate;
	private newMultipleItemRequirement: MultipleItemRequirement<ComponentTemplate>;
	private multiItemTemplateUnderEdit?: MultipleItemRequirement<ComponentTemplate>;
	private requestTimeoutId?: number;

	public constructor(props: any) {

		super(props);
		this.state = {
			visibleSection: "template-requirement-selector",
			genericComponentTemplates: [],
			newTemplateList: [],
			selectedMultiItemTemplates: [],
			newMultiItemRequirementItems: [],
			loading: true,
			creatingMultipleItemRequirement: false,
			creatingSingleItemRequirement: false,
			multiItemRequirementPhase: "selection",
			waitingForUpdate: false,
			editingMetaData: false,
		};

		// Prep Multi Item under edit
		this.multiItemTemplateUnderEdit = undefined;

		// Prep new multiItem
		this.newMultipleItemRequirement = new MultipleItemRequirement("ComponentTemplate");
		this.newMultipleItemRequirement.requiredness = Requiredness.OPTIONAL;
		this.newMultipleItemRequirement.requirementType = MultipleItemRequirementType.All;
		this.newMultipleItemRequirement.fulfillmentCount = 0;
		this.newMultipleItemRequirement.items = this.state.newMultiItemRequirementItems;
	}

	public componentWillMount = async () => {

		// Setup unload method for if we hit a refresh
		window.addEventListener("beforeunload", this.onUnload);

		// Get section template
		const { id } = this.props.match.params;

		// If we are at /header show metadata
		if (this.props.location.pathname.match(/header/i) && !this.state.editingMetaData) {

			StateService.setCancelableState(() => this.setState({ editingMetaData: true }), this.apiCancellationToken);
		} else {

			StateService.setCancelableState(() => this.setState({ editingMetaData: false }), this.apiCancellationToken);
		}

		let sectionTemplate: SectionTemplate | undefined;

		if (id) {

			sectionTemplate = await SectionTemplateApi.getSectionTemplateById(id, undefined);
		}

		const loading = false;
		const genericComponentTemplates = await ComponentTemplateApi.getComponentTemplates(true);
		this.sectionTemplate = sectionTemplate;
		StateService.setCancelableState(() => this.setState({ genericComponentTemplates, loading }), this.apiCancellationToken);
	}

	public componentWillUnmount = () => {

		// Cancel Outstanding API Calls
		this.apiCancellationToken.cancel();

		window.removeEventListener("beforeunload", this.onUnload);
	}

	public componentWillUpdate = async (nextProps: ITemplateHierarchyProps) => {

		// If we are at /header show metadata
		const containsHeaders = nextProps.location.pathname.match(/header/i) || false;

		if (containsHeaders && !this.state.editingMetaData) {

			StateService.setCancelableState(() => this.setState({ editingMetaData: true }), this.apiCancellationToken);
		}
		else if (!containsHeaders && this.state.editingMetaData) {

			StateService.setCancelableState(() => this.setState({ editingMetaData: false }), this.apiCancellationToken);
		}

		const { id } = nextProps.match.params;
		let sectionTemplate: SectionTemplate | undefined;

		if (id !== this.props.match.params.id) {

			StateService.setCancelableState(() => this.setState({ loading: true }), this.apiCancellationToken);
			sectionTemplate = await SectionTemplateApi.getSectionTemplateById(id, undefined);
			const loading = false;
			const genericComponentTemplates = await ComponentTemplateApi.getComponentTemplates(true);
			this.sectionTemplate = sectionTemplate;
			StateService.setCancelableState(() => this.setState({ genericComponentTemplates, loading }), this.apiCancellationToken);
		}

	}

	public onTemplateDrop = async ({ oldIndex, newIndex }: any) => {

		// if the section template is avalible and the old and new index are not the same
		if (this.sectionTemplate && oldIndex !== newIndex && this.sectionTemplate.id) {

			// Adjust placement in Template Reuirements
			this.sectionTemplate.templateRequirementList = arrayMove(this.sectionTemplate.templateRequirementList, oldIndex, newIndex);

			// Assign new sort order on the item itself(modifies this.sectionTemplate.requirements[0].item.initialOrder)
			this.sectionTemplate.templateRequirementList.forEach((item, index) => {

				item.initialOrder = index;
			});

			this.forceUpdate();

			// send off changes
			// update all changes on the screen with this.sectionTemplate
			SectionTemplateApi.updateSectionTemplate(this.sectionTemplate.id, this.sectionTemplate);
		}
	}

	public onUnload = () => {

		// If there is something pending run it before the page unloads
		if (this.requestTimeoutId) {

			this.softSectionTimeOutMethod(this.requestTimeoutId);
		}
	}

	public softSectionTimeOutMethod = async (requestTimeoutId: number) => {

		if (this.sectionTemplate && this.sectionTemplate.id) {

			const sectionTemplate = await SectionTemplateApi.updateSectionTemplate(this.sectionTemplate.id, this.sectionTemplate);

			if (sectionTemplate !== undefined) {

				notification.success({
					message: "Section updated successfully"
				});
			}
			else {

				notification.error({
					message: "Section update failed"
				});
			}

			// Clean up the requestTimeoutId if nothing else has been called
			if (this.requestTimeoutId === requestTimeoutId) {

				this.requestTimeoutId = undefined;
			}
		}
	}

	public softSectionTemplateUpdate = async () => {

		if (this.requestTimeoutId) {

			window.clearTimeout(this.requestTimeoutId);
			this.requestTimeoutId = undefined;
		}

		if (this.sectionTemplate) {

			this.forceUpdate();

			const requestTimeoutId = window.setTimeout(
				async () => {

					this.softSectionTimeOutMethod(requestTimeoutId);
				}, 1000
			);

			this.requestTimeoutId = requestTimeoutId;


		}
	}

	public hardSectionTemplateUpdate = async (callback?: () => void) => {
		if (this.sectionTemplate && this.sectionTemplate.id) {

			let sectionTemplate;

			StateService.setCancelableState(() => this.setState({ waitingForUpdate: true }), this.apiCancellationToken);
			sectionTemplate = await SectionTemplateApi.updateSectionTemplate(this.sectionTemplate.id, this.sectionTemplate);
			StateService.setCancelableState(() => this.setState({ waitingForUpdate: false }), this.apiCancellationToken);

			this.sectionTemplate = sectionTemplate;

			if (sectionTemplate !== undefined) {

				notification.success({
					message: "Successfully Updated",
					description: `${sectionTemplate.name} has been successfully updated`
				});
			}
			else {

				notification.error({
					message: "Update Failed"
				});
			}

			this.forceUpdate();

			if (callback) {

				callback();
			}
		}

	}

	public changeSectionTemplateName = (newName: string) => {

		if (this.sectionTemplate) {

			this.sectionTemplate.name = newName;
			this.softSectionTemplateUpdate();
		}
	}

	public newMultipleItemClick = () => {

		StateService.setCancelableState(() => this.setState({ creatingMultipleItemRequirement: true }), this.apiCancellationToken);
	}

	public newSingleItemClick = () => {

		StateService.setCancelableState(() => this.setState({ creatingSingleItemRequirement: true }), this.apiCancellationToken);
	}

	public addToNewTemplateList = (template: ComponentTemplate) => {

		const selectedMultiItemTemplates = [...this.state.selectedMultiItemTemplates];
		selectedMultiItemTemplates.push(template);
		StateService.setCancelableState(() => this.setState({ selectedMultiItemTemplates }), this.apiCancellationToken);
	}

	public addToTemplateMultiItemRequirement = (index: number, listName: string) => {

		if (listName === "genericComponentTemplates") {

			const selectedMultiItemTemplates = [...this.state.selectedMultiItemTemplates];
			const genericComponentTemplates = [...this.state.genericComponentTemplates];
			const template = genericComponentTemplates.splice(index, 1)[0];

			selectedMultiItemTemplates.push(template);
			StateService.setCancelableState(() => this.setState({ genericComponentTemplates, selectedMultiItemTemplates }), this.apiCancellationToken);
		}

		else if (listName === "selectedMultiItemTemplates") {

			const genericComponentTemplates = [...this.state.genericComponentTemplates];
			const selectedMultiItemTemplates = [...this.state.selectedMultiItemTemplates];
			const template = selectedMultiItemTemplates.splice(index, 1)[0];

			genericComponentTemplates.push(template);
			StateService.setCancelableState(() => this.setState({ genericComponentTemplates, selectedMultiItemTemplates }), this.apiCancellationToken);

		}
	}

	public showSelectionInConfiguration = async () => {

		const selectedMultiItemTemplates = [...this.state.selectedMultiItemTemplates];

		// Make sure there was a selection made
		if (selectedMultiItemTemplates.length) {

			// grap all the items selected and add them into an array of requirementItems
			const items: Array<RequirementItem<ComponentTemplate>> = selectedMultiItemTemplates.map((componentTemplate, key) => {

				// create a new requirementItem
				const newRequirementItem = new RequirementItem<ComponentTemplate>("ComponentTemplate");

				// Setup initial order based on current order
				newRequirementItem.initialOrder = key;

				// create circular reference to parent
				newRequirementItem.owningRequirement = this.newMultipleItemRequirement;

				// Assign component to requirement item
				newRequirementItem.item = componentTemplate;

				return newRequirementItem;
			});

			// add new requirements to state element
			await StateService.setCancelableState(() => this.setState({ newMultiItemRequirementItems: items }), this.apiCancellationToken);

			// add new requirements to local member
			this.newMultipleItemRequirement.items = this.state.newMultiItemRequirementItems;

			// force update to reflect changes
			await this.forceUpdate();

			// Move on to next phase in multiItem creation
			StateService.setCancelableState(() => this.setState({ multiItemRequirementPhase: "configuration" }), this.apiCancellationToken);
		}
		else {
			message.error("Please select at least one template.", 3);
		}
	}

	public updateMultiItemRequirement = () => {

		this.forceUpdate();
	}

	public resetMultiItemProcess = () => {

		// close modal
		StateService.setCancelableState(() => this.setState({ creatingMultipleItemRequirement: false }), this.apiCancellationToken);

		// Set multiItem process to first item
		StateService.setCancelableState(() => this.setState({ multiItemRequirementPhase: "selection" }), this.apiCancellationToken);

		// Clear out multiItemTemplateUnder Edit to prepare for a new item
		this.multiItemTemplateUnderEdit = undefined;

		// Build a new Multi Item Requirement in preperation to add a new req
		this.newMultipleItemRequirement = new MultipleItemRequirement("ComponentTemplate");
		this.newMultipleItemRequirement.requiredness = Requiredness.OPTIONAL;
		this.newMultipleItemRequirement.fulfillmentCount = 0;
		this.newMultipleItemRequirement.items = this.state.newMultiItemRequirementItems;

		// push all selected into generic and then reset when we get the call back
		const { genericComponentTemplates, selectedMultiItemTemplates } = this.state;

		// filter out any new items from the list
		const filteredSelectedMultiItemTemplates = selectedMultiItemTemplates.filter((item) => !item.name.toLowerCase().includes("new"));

		// repopulate the generic template components list and set selected templates back to nothing
		StateService.setCancelableState(
			() => this.setState({ selectedMultiItemTemplates: [], genericComponentTemplates: [...genericComponentTemplates, ...filteredSelectedMultiItemTemplates] }),
			this.apiCancellationToken
		);
	}

	public addMultiItemRequirementToSectionTemplate = async () => {

		if (this.sectionTemplate) {

			// Mark id as null so it will be picked up by the server as a new requirement
			this.newMultipleItemRequirement.id = undefined;

			// Clone new multi template requirement into a local version we can prepare for insertion
			const newMultipleItemRequirement = cloneDeep(this.newMultipleItemRequirement);

			// Setup initial order so that it adds each item to the end of the templateRequirementsList
			newMultipleItemRequirement.items.forEach((item, key) => item.initialOrder = this.sectionTemplate ? this.sectionTemplate.templateRequirementList.length + key : 0);

			// Add to requirements list
			this.sectionTemplate.requirements.push(newMultipleItemRequirement);

			// Store new section template and push out to the API, lock down the display, and replace the sectionTemplate memeber with returned object
			this.hardSectionTemplateUpdate(this.resetMultiItemProcess);
		}
	}

	public onTemplateEdit = (multipleItemRequirement: MultipleItemRequirement<ComponentTemplate>) => {

		this.multiItemTemplateUnderEdit = multipleItemRequirement;
		this.forceUpdate();
	}

	public updateMultiItemRequirementUnderEdit = () => {

		this.forceUpdate();
	}

	public closeMultiItemRequirementEditModal = () => {

		this.multiItemTemplateUnderEdit = undefined;
		this.forceUpdate();
	}

	public onTemplateDelete = (itemRequirement: Requirement) => {

		if (this.sectionTemplate) {

			// set requirements to a new list that filters out the itemRequirement passed in
			this.sectionTemplate.requirements = this.sectionTemplate.requirements.filter((requirement) => requirement.id !== itemRequirement.id);

			// set templateRequirementList to a new list that filters out the itemRequirement passed in
			this.sectionTemplate.templateRequirementList = this.sectionTemplate.templateRequirementList.filter((requirement) => requirement.owningRequirement.id !== itemRequirement.id);

			// run a soft update of the list so that it will immediatly execute and not wait on the api response
			this.softSectionTemplateUpdate();
		}
	}

	public newComponentTemplate = async (componentTemplate: ComponentTemplate) => {

		if (this.sectionTemplate) {

			// Create Single Item Requirement
			const singleItemRequirement = new SingleItemRequirement<ComponentTemplate>("ComponentTemplate");

			// default to non repeatable
			singleItemRequirement.isRepeatable = false;

			// default to optional(no required)
			singleItemRequirement.requiredness = Requiredness.OPTIONAL;

			// Create requirement item
			const requirementItem = new RequirementItem<ComponentTemplate>("ComponentTemplate");

			// attach component to requirement item
			requirementItem.item = componentTemplate;

			// create circular ref to parent
			requirementItem.owningRequirement = new Requirement("<ComponentTemplate>");

			// make sure the item appears at the end of the requirements list
			requirementItem.initialOrder = this.sectionTemplate.templateRequirementList.length + 1;

			// Add requirement item
			singleItemRequirement.item = requirementItem;

			// Add item to requierments list
			this.sectionTemplate.requirements.push(singleItemRequirement);

			// trigger a hard update and close the modal when finished
			this.hardSectionTemplateUpdate(() => { StateService.setCancelableState(() => this.setState({ creatingSingleItemRequirement: false }), this.apiCancellationToken); });

		}
	}

	public editingToggle = () => {

		// Grab url path
		const { pathname } = this.props.location;

		// if /header/ is in the url
		if (pathname.match(/header/i)) {

			// replace /header with nothing (route me back to the section)
			const newPathname = pathname.replace("/header", "");

			// push the new url out through react router
			this.props.history.push(newPathname);

			// change the editing metadata state to false
			StateService.setCancelableState(() => this.setState({ editingMetaData: false }), this.apiCancellationToken);
		} else {

			// Append header on to the pathname
			const newPathname = pathname + "/header";

			// push the new url out through react router
			this.props.history.push(newPathname);

			// change the editing metadata state to true
			StateService.setCancelableState(() => this.setState({ editingMetaData: true }), this.apiCancellationToken);
		}

	}

	public toggleCanReorderChildren = () => {

		if (this.sectionTemplate) {

			const { canReorderChildren } = this.sectionTemplate;
			this.sectionTemplate.canReorderChildren = !canReorderChildren;
			this.softSectionTemplateUpdate();
		}
	}

	public toggleAreRequirementsEnforced = () => {

		if (this.sectionTemplate) {

			const { areRequirementsEnforced } = this.sectionTemplate;
			this.sectionTemplate.areRequirementsEnforced = !areRequirementsEnforced;
			this.softSectionTemplateUpdate();
		}
	}

	public render() {

		// Render Normally
		return (
			// <Secure redirectOnDenial="/">
			<div>
				<div className="template-hierarchy">
					<Spin spinning={this.state.loading}>
						<TransitionRoute on={this.state.visibleSection} case={"template-requirement-selector"}>
							<div>
								{
									this.sectionTemplate ? (
										<div>
											<TemplateTitle templateName={this.sectionTemplate.name} onNameChange={this.changeSectionTemplateName} />
											<Breadcrumb />
											<TransitionRoute on={this.state.editingMetaData} case={true}>
												<div>
													<MetaData
														toggleEditing={this.editingToggle}
														waitingForUpdate={this.state.waitingForUpdate}
														softSectionTemplateUpdate={this.softSectionTemplateUpdate}
														sectionTemplate={this.sectionTemplate}
														hardSectionTemplateUpdate={this.hardSectionTemplateUpdate}
													/>
												</div>
											</TransitionRoute>
											<TransitionRoute on={this.state.editingMetaData} case={false}>
												<div>
													<Button type="primary" icon="edit" onClick={this.editingToggle} >Edit Section Header Requirements</Button>
													<div className="global-switches">
														{/* Requirements enforced<Switch checked={this.sectionTemplate.areRequirementsEnforced} onChange={this.toggleAreRequirementsEnforced} /> */}
														Order enforced <Switch size="small" checked={!this.sectionTemplate.canReorderChildren} onChange={this.toggleCanReorderChildren} />
														<InfoQuestion title="When creating a section the user will be forced to implement sections in this order." />
													</div>
													<SortableContainer onSortEnd={this.onTemplateDrop} axis="xy" className="template-requirement-selector" >
														<NewRequirement newSingleItemClick={this.newSingleItemClick} newMultipleItemClick={this.newMultipleItemClick} />
														<TemplateRequirementSelector
															onTemplateEdit={this.onTemplateEdit}
															onTemplateDelete={this.onTemplateDelete}
															sectionTemplate={this.sectionTemplate}
															templateRequirementList={this.sectionTemplate.templateRequirementList}
															genericComponentTemplates={this.state.genericComponentTemplates || []}
															softSectionTemplateUpdate={this.softSectionTemplateUpdate}
															hardSectionTemplateUpdate={this.hardSectionTemplateUpdate}
															loading={this.state.loading}
														/>
													</SortableContainer>
												</div>
											</TransitionRoute>
											<div>
												{/* NEW MULTI ITEM TEMPLATE */}
												<Modal
													footer={
														this.state.multiItemRequirementPhase === "selection"
															?
															[
																<Button
																	onClick={this.showSelectionInConfiguration}
																	key="multiple-item-requirement-next-step-button"
																>
																	Next<Icon type="right" />
																</Button>
															]
															:
															[
																<Button
																	type="primary"
																	onClick={this.addMultiItemRequirementToSectionTemplate}
																	key="multiple-item-requirement-save-button"
																>
																	Save
																</Button>,
																<Button
																	icon="left"
																	onClick={() => StateService.setCancelableState(() => this.setState({ multiItemRequirementPhase: "selection" }), this.apiCancellationToken)}
																	key="multiple-item-requirement-back-button"
																>
																	Back
																</Button>
															]

													}
													visible={this.state.creatingMultipleItemRequirement}
													onCancel={this.state.waitingForUpdate ? () => null : this.resetMultiItemProcess}
												>
													<Spin spinning={this.state.waitingForUpdate} indicator={<Icon type="loading" spin={true} />}>
														<TransitionRoute on={this.state.multiItemRequirementPhase} case={"selection"}>
															<div>
																<GenericTemplateSelector
																	selectedMultiItemTemplates={this.state.selectedMultiItemTemplates}
																	addToTemplateMultiItemRequirement={this.addToTemplateMultiItemRequirement}
																	addToNewTemplateList={this.addToNewTemplateList}
																	genericComponentTemplates={this.state.genericComponentTemplates || []}
																/>
															</div>
														</TransitionRoute>
														<TransitionRoute on={this.state.multiItemRequirementPhase} case={"configuration"}>
															<div>
																<MultipleItemConfiguration updateMultiItemRequirement={this.updateMultiItemRequirement} multipleItemRequirement={this.newMultipleItemRequirement} />
															</div>
														</TransitionRoute>
													</Spin>
												</Modal>
												{/* MULTI ITEM TEMPLATE EDIT */}
												<Modal
													footer={
														[
															<Button
																type="primary"
																onClick={() => this.hardSectionTemplateUpdate(this.closeMultiItemRequirementEditModal)}
																key="multiple-item-requirement-edit-save-button"
															>
																Save
															</Button>
														]
													}
													visible={!!this.multiItemTemplateUnderEdit}
													onCancel={this.state.waitingForUpdate ? () => null : this.closeMultiItemRequirementEditModal}
												>
													<Spin spinning={this.state.waitingForUpdate} indicator={<Icon type="loading" spin={true} />}>
														<MultipleItemConfiguration updateMultiItemRequirement={this.updateMultiItemRequirementUnderEdit} multipleItemRequirement={this.multiItemTemplateUnderEdit} />
													</Spin>
												</Modal>
												{/* SINGLE ITEM TEMPLATE CREATE */}
												<Modal
													visible={this.state.creatingSingleItemRequirement}
													onCancel={this.state.waitingForUpdate ? () => null : () => StateService.setCancelableState(() => this.setState({ creatingSingleItemRequirement: false }), this.apiCancellationToken)}
												>
													<Spin spinning={this.state.waitingForUpdate} indicator={<Icon type="loading" spin={true} />}>
														<GenericTemplateSelectorSingle genericComponentTemplates={this.state.genericComponentTemplates || []} templateSelected={this.newComponentTemplate} />
													</Spin>
												</Modal>
											</div>
										</div>
									)
										: null
								}
							</div>
						</TransitionRoute>
					</Spin>
				</div>
			</div>
			// </Secure>
		);
	}
}
